/**
 * ScrollToTop — global route-change scroll handler.
 *
 * Rules:
 *   • Any navigation WITH a hash (e.g. `/terms#refund-eligibility`) →
 *     scroll the anchored element into view. Retries once on next tick to
 *     cover the case where the target section hasn't mounted yet.
 *   • POP navigation without a hash (Back / Forward) → do nothing so the
 *     browser can restore the user's previous scroll position naturally.
 *   • Everything else (PUSH / REPLACE without a hash) → jump the window to
 *     (0, 0) instantly so every new page opens at the very top (fixes
 *     footer policy links opening mid-page).
 *
 * Mount once, directly inside <BrowserRouter> and above <Routes>.
 * Renders nothing.
 */
import { useEffect } from 'react';
import { useLocation, useNavigationType } from 'react-router-dom';

const scrollToAnchor = (id) => {
  if (!id) return false;
  const el = document.getElementById(id);
  if (!el) return false;
  el.scrollIntoView({ behavior: 'instant', block: 'start' });
  return true;
};

const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const navigationType = useNavigationType(); // 'PUSH' | 'REPLACE' | 'POP'

  useEffect(() => {
    // Intentional in-page anchor — jump to it regardless of nav type.
    // If the target hasn't mounted yet (React is still rendering the page),
    // retry once on the next animation frame.
    if (hash) {
      const id = hash.slice(1);
      if (!scrollToAnchor(id)) {
        requestAnimationFrame(() => scrollToAnchor(id));
      }
      return;
    }

    // Back / Forward navigation → let the browser restore the previous
    // scroll position instead of forcing a jump to the top.
    if (navigationType === 'POP') return;

    // Default: jump straight to the top of the new page.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
  }, [pathname, hash, navigationType]);

  return null;
};

export default ScrollToTop;
